import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, GripVertical, Image as ImageIcon } from 'lucide-react';
import KatexRenderer from './KatexRenderer';
import HtmlMathPreview from './HtmlMathPreview';
import MathToolbar from './MathToolbar';

/**
 * Rich Text Editor with Inline Image Support
 * Allows text and images to be mixed together with drag-and-drop positioning
 */
const RichTextEditorWithImages = ({
  value = '',
  onChange,
  placeholder = "Enter text here...",
  label = "",
  className = "",
  showPreview = true,
  showToolbar = true,
  rows = 4,
  disabled = false,
  required = false,
  onImageUpload
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedImageId, setDraggedImageId] = useState(null);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageCounterRef = useRef(0);

  // Parse value to extract images and text
  const parseContent = (htmlContent) => {
    if (!htmlContent) return { text: '', images: [] };
    
    // Check if content is HTML with images
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const images = tempDiv.querySelectorAll('img[data-image-id]');
    
    const imageData = Array.from(images).map(img => ({
      id: img.getAttribute('data-image-id'),
      src: img.src,
      alt: img.alt || '',
      position: img.getAttribute('data-position') || 'inline'
    }));

    return { text: htmlContent, images: imageData };
  };

  // Convert content to HTML string
  const contentToHtml = (content) => {
    if (typeof content === 'string') {
      return content;
    }
    // If content is an object with text and images, reconstruct HTML
    if (content && content.text) {
      return content.text;
    }
    return '';
  };

  const currentContent = contentToHtml(value);
  const { images: parsedImages } = parseContent(currentContent);

  const insertAtCursor = (text) => {
    if (isPreviewMode) {
      setIsPreviewMode(false);
    }
    
    setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const selection = window.getSelection();
      let range;
      
      // Check if there's a valid selection range
      if (selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
      } else {
        // Create a range at the end of the editor if no selection exists
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }
      
      // Insert text node
      const textNode = document.createTextNode(text);
      range.insertNode(textNode);
      
      // Move cursor after inserted text
      range.setStartAfter(textNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      
      // Update content
      updateContent();
    }, 100);
  };

  const updateContent = () => {
    if (!editorRef.current) return;
    
    const html = editorRef.current.innerHTML;
    onChange(html);
  };

  const handleImageUpload = async (file, displayMode = 'auto') => {
    if (!file || !file.type.startsWith('image/')) {
      return;
    }

    // Create object URL for preview
    const objectUrl = URL.createObjectURL(file);
    const imageId = `img-${Date.now()}-${imageCounterRef.current++}`;

    // If onImageUpload callback is provided, use it to upload to storage
    let finalUrl = objectUrl;
    if (onImageUpload) {
      try {
        const result = await onImageUpload(file, imageId);
        if (result && result.url) {
          finalUrl = result.url;
          // Revoke object URL since we have the uploaded URL
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        // Keep using object URL as fallback
      }
    }

    // Insert image at cursor position
    insertImageAtCursor(finalUrl, imageId, displayMode);
  };

  const insertImageAtCursor = (imageUrl, imageId, displayMode = 'auto') => {
    if (isPreviewMode) {
      setIsPreviewMode(false);
    }

    setTimeout(() => {
      const editor = editorRef.current;
      if (!editor) return;

      const selection = window.getSelection();
      let range;
      
      if (selection.rangeCount > 0) {
        range = selection.getRangeAt(0);
      } else {
        range = document.createRange();
        range.selectNodeContents(editor);
        range.collapse(false);
      }

      // Determine display mode
      let isBlock = false;
      if (displayMode === 'block') {
        isBlock = true;
      } else if (displayMode === 'auto') {
        // Auto-detect: if at end of line/paragraph or after newline, use block
        const container = range.commonAncestorContainer;
        let textBefore = '';
        let isAtLineEnd = false;
        let isAfterNewline = false;
        let isEmptyLine = false;
        
        // Get text content before cursor
        if (container.nodeType === Node.TEXT_NODE) {
          textBefore = container.textContent?.substring(0, range.startOffset) || '';
          isAtLineEnd = range.startOffset === (container.textContent?.length || 0);
        } else {
          // For element nodes, check the entire content
          const walker = document.createTreeWalker(
            container,
            NodeFilter.SHOW_TEXT,
            null
          );
          let node;
          while (node = walker.nextNode()) {
            if (node === range.startContainer || node.parentNode === range.startContainer) {
              textBefore += node.textContent?.substring(0, range.startOffset) || '';
              break;
            }
            textBefore += node.textContent || '';
          }
          isAtLineEnd = range.startOffset === (range.startContainer.textContent?.length || 0);
        }
        
        // Check for line breaks or empty content
        isAfterNewline = textBefore.endsWith('\n') || 
                        textBefore.endsWith('<br>') || 
                        textBefore.endsWith('<br/>') ||
                        textBefore.endsWith('<br />');
        isEmptyLine = !textBefore.trim() || textBefore.trim().length === 0;
        
        // Check if cursor is at the end of the editor
        const editorText = editor.textContent || '';
        const cursorPosition = range.startOffset;
        const isAtEditorEnd = cursorPosition >= editorText.length - 1;
        
        // Use block mode if at end of line, after newline, empty line, or at end of editor
        if (isAtLineEnd || isAfterNewline || isEmptyLine || isAtEditorEnd) {
          isBlock = true;
        }
      }

      // Create image element
      const img = document.createElement('img');
      img.src = imageUrl;
      img.setAttribute('data-image-id', imageId);
      img.setAttribute('data-display-mode', isBlock ? 'block' : 'inline');
      img.className = isBlock 
        ? 'block-image max-w-full h-auto max-h-96 cursor-move rounded border border-gray-300 my-4 mx-auto'
        : 'inline-image max-w-full h-auto max-h-64 cursor-move rounded border border-gray-300 my-2';
      
      if (isBlock) {
        img.style.display = 'block';
        img.style.margin = '8px auto';
        img.style.maxWidth = '100%';
      } else {
        img.style.display = 'inline-block';
        img.style.verticalAlign = 'middle';
        img.style.maxWidth = '300px';
      }
      
      img.draggable = true;
      img.contentEditable = false;
      
      // Add drag handlers
      img.addEventListener('dragstart', handleImageDragStart);
      img.addEventListener('dragend', handleImageDragEnd);
      img.addEventListener('dblclick', (e) => {
        e.stopPropagation();
        // Toggle between inline and block on double-click
        toggleImageDisplayMode(imageId);
      });
      img.addEventListener('click', (e) => {
        e.stopPropagation();
        // Select image for potential deletion
      });

      // Insert image
      if (isBlock) {
        // For block images, insert with line breaks
        const br = document.createElement('br');
        range.insertNode(br);
        range.setStartAfter(br);
        range.insertNode(img);
        const brAfter = document.createElement('br');
        range.setStartAfter(img);
        range.insertNode(brAfter);
      } else {
        // For inline images, insert normally
        range.insertNode(img);
        // Add a space after image for better editing
        const space = document.createTextNode(' ');
        range.setStartAfter(img);
        range.insertNode(space);
        range.setStartAfter(space);
      }
      
      // Update cursor position
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      updateContent();
    }, 100);
  };

  const toggleImageDisplayMode = (imageId) => {
    const editor = editorRef.current;
    if (!editor) return;

    const img = editor.querySelector(`img[data-image-id="${imageId}"]`);
    if (!img) return;

    const currentMode = img.getAttribute('data-display-mode') || 'inline';
    const newMode = currentMode === 'inline' ? 'block' : 'inline';

    img.setAttribute('data-display-mode', newMode);

    if (newMode === 'block') {
      img.className = 'block-image max-w-full h-auto max-h-96 cursor-move rounded border border-gray-300 my-4 mx-auto';
      img.style.display = 'block';
      img.style.margin = '8px auto';
      img.style.maxWidth = '100%';
      img.style.verticalAlign = '';
    } else {
      img.className = 'inline-image max-w-full h-auto max-h-64 cursor-move rounded border border-gray-300 my-2';
      img.style.display = 'inline-block';
      img.style.verticalAlign = 'middle';
      img.style.maxWidth = '300px';
      img.style.margin = '2px 4px';
    }

    updateContent();
  };

  const handleImageDragStart = (e) => {
    setIsDragging(true);
    const imageId = e.target.getAttribute('data-image-id');
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleImageDragEnd = (e) => {
    e.target.style.opacity = '1';
    setIsDragging(false);
    setDraggedImageId(null);
  };

  const handleEditorDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const editor = editorRef.current;
    if (!editor) return;

    const selection = window.getSelection();
    let range;

    if (selection.rangeCount > 0) {
      range = selection.getRangeAt(0);
    } else {
      range = document.createRange();
      range.selectNodeContents(editor);
      range.collapse(false);
    }

    // Check if dropping an image
    const imageHtml = e.dataTransfer.getData('text/html');
    if (imageHtml) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = imageHtml;
      const img = tempDiv.querySelector('img');
      
      if (img) {
        // Remove old image if it exists
        const oldImage = editor.querySelector(`img[data-image-id="${img.getAttribute('data-image-id')}"]`);
        if (oldImage) {
          oldImage.remove();
        }

        // Get display mode and apply styling
        const displayMode = img.getAttribute('data-display-mode') || 'inline';
        if (displayMode === 'block') {
          img.style.display = 'block';
          img.style.margin = '8px auto';
          img.style.maxWidth = '100%';
          img.className = 'block-image max-w-full h-auto max-h-96 cursor-move rounded border border-gray-300 my-4 mx-auto';
        } else {
          img.style.display = 'inline-block';
          img.style.verticalAlign = 'middle';
          img.style.maxWidth = '300px';
          img.style.margin = '2px 4px';
          img.className = 'inline-image max-w-full h-auto max-h-64 cursor-move rounded border border-gray-300 my-2';
        }
        img.draggable = true;
        img.contentEditable = false;
        
        // Reattach drag handlers
        img.addEventListener('dragstart', handleImageDragStart);
        img.addEventListener('dragend', handleImageDragEnd);
        img.addEventListener('dblclick', () => {
          toggleImageDisplayMode(img.getAttribute('data-image-id'));
        });

        // Insert at drop position
        range.insertNode(img);
        
        // Add space after
        const space = document.createTextNode(' ');
        range.setStartAfter(img);
        range.insertNode(space);
        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);

        updateContent();
        return;
      }
    }

    // Handle file drop
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      // Auto-detect: if dropping at end of line, use block mode
      const container = range.commonAncestorContainer;
      const textBefore = container.textContent || '';
      const isAtLineEnd = range.startOffset === (container.textContent?.length || 0);
      const displayMode = isAtLineEnd ? 'block' : 'auto';
      handleImageUpload(files[0], displayMode);
    }
  };

  const handleEditorDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
  };

  const removeImage = (imageId) => {
    const editor = editorRef.current;
    if (!editor) return;

    const img = editor.querySelector(`img[data-image-id="${imageId}"]`);
    if (img) {
      img.remove();
      updateContent();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const clipboardData = e.clipboardData;
    
    // Handle image paste
    if (clipboardData.items) {
      for (let i = 0; i < clipboardData.items.length; i++) {
        const item = clipboardData.items[i];
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          // Auto-detect mode for pasted images
          handleImageUpload(file, 'auto');
          return;
        }
      }
    }

    // Handle text paste
    const text = clipboardData.getData('text/plain');
    if (text) {
      insertAtCursor(text);
    }
  };

  // Initialize editor content and reattach event listeners
  useEffect(() => {
    if (editorRef.current && !isPreviewMode) {
      // Only update if content is different to avoid cursor jumping
      if (editorRef.current.innerHTML !== currentContent) {
        editorRef.current.innerHTML = currentContent || '';
      }
      
      // Reattach drag handlers to images after content update
      const images = editorRef.current.querySelectorAll('img[data-image-id]');
      images.forEach(img => {
        // Remove existing listeners to avoid duplicates
        const newImg = img.cloneNode(true);
        img.parentNode.replaceChild(newImg, img);
        
        // Get display mode
        const displayMode = newImg.getAttribute('data-display-mode') || 'inline';
        
        // Reattach handlers
        newImg.addEventListener('dragstart', handleImageDragStart);
        newImg.addEventListener('dragend', handleImageDragEnd);
        newImg.addEventListener('dblclick', () => {
          toggleImageDisplayMode(newImg.getAttribute('data-image-id'));
        });
        
        // Apply correct styling based on mode
        if (displayMode === 'block') {
          newImg.style.display = 'block';
          newImg.style.margin = '8px auto';
          newImg.style.maxWidth = '100%';
          newImg.className = 'block-image max-w-full h-auto max-h-96 cursor-move rounded border border-gray-300 my-4 mx-auto';
        } else {
          newImg.style.display = 'inline-block';
          newImg.style.verticalAlign = 'middle';
          newImg.style.maxWidth = '300px';
          newImg.style.margin = '2px 4px';
          newImg.className = 'inline-image max-w-full h-auto max-h-64 cursor-move rounded border border-gray-300 my-2';
        }
        
        newImg.draggable = true;
        newImg.contentEditable = false;
      });
    }
  }, [currentContent, isPreviewMode]);

  // Render preview with images and math
  const renderPreview = () => {
    if (!currentContent) {
      return <div className="text-gray-400 italic">{placeholder}</div>;
    }

    // Check if content contains HTML (images)
    const hasHtml = currentContent.includes('<img') || 
                    currentContent.includes('<p>') || 
                    currentContent.includes('<div>') ||
                    currentContent.includes('<br');

    if (hasHtml) {
      // Use HtmlMathPreview for content with HTML/images
      return <HtmlMathPreview content={currentContent} />;
    } else {
      // Pure text content, use KatexRenderer for proper math rendering
      return <KatexRenderer content={currentContent} />;
    }
  };

  return (
    <div className={`rich-text-editor-with-images ${className}`}>
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {/* Math Toolbar */}
          {showToolbar && (
            <MathToolbar 
              onInsert={insertAtCursor}
            />
          )}

          {/* Image Upload Buttons */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                fileInputRef.current?.setAttribute('data-display-mode', 'inline');
                fileInputRef.current?.click();
              }}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm flex items-center gap-1"
              disabled={disabled || isPreviewMode}
              title="Insert inline image"
            >
              <ImageIcon size={16} />
              Inline
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                fileInputRef.current?.setAttribute('data-display-mode', 'block');
                fileInputRef.current?.click();
              }}
              className="px-3 py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 text-sm flex items-center gap-1"
              disabled={disabled || isPreviewMode}
              title="Insert block image (for diagrams at end of question)"
            >
              <ImageIcon size={16} />
              Block
            </button>
          </div>
        </div>

        {/* Preview Toggle */}
        {showPreview && (
          <div className="flex gap-2">
            <button
              onClick={() => setIsPreviewMode(false)}
              className={`px-3 py-1 rounded-md text-sm flex items-center ${
                !isPreviewMode 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => setIsPreviewMode(true)}
              className={`px-3 py-1 rounded-md text-sm flex items-center ${
                isPreviewMode 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Preview
            </button>
          </div>
        )}
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            const displayMode = e.target.getAttribute('data-display-mode') || 'auto';
            handleImageUpload(file, displayMode);
            e.target.removeAttribute('data-display-mode');
          }
          e.target.value = ''; // Reset input
        }}
        className="hidden"
      />

      {/* Editor/Preview Area */}
      {isPreviewMode ? (
        <div 
          className="min-h-[120px] p-4 border border-gray-300 rounded-md bg-white"
          style={{ minHeight: `${rows * 1.5}rem` }}
        >
          {renderPreview()}
        </div>
      ) : (
        <div
          ref={editorRef}
          contentEditable={!disabled}
          onInput={updateContent}
          onDrop={handleEditorDrop}
          onDragOver={handleEditorDragOver}
          onPaste={handlePaste}
          className="w-full min-h-[120px] p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y overflow-auto"
          style={{ 
            minHeight: `${rows * 1.5}rem`,
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}
          data-placeholder={placeholder}
          suppressContentEditableWarning={true}
        />
      )}

      {/* Placeholder styling */}
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          font-style: italic;
        }
        .inline-image {
          display: inline-block !important;
          vertical-align: middle !important;
          max-width: 300px !important;
          height: auto;
          margin: 2px 4px;
        }
        .block-image {
          display: block !important;
          margin: 8px auto !important;
          max-width: 100% !important;
          height: auto;
        }
        .inline-image:hover,
        .block-image:hover {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        [contenteditable] .inline-image,
        [contenteditable] .block-image {
          cursor: move;
        }
      `}</style>

      {/* Help Text */}
      {!isPreviewMode && (
        <div className="mt-2 text-xs text-gray-500">
          <p><strong>Tips:</strong></p>
          <div className="space-y-1">
            <div>• Type text and insert images inline or as block</div>
            <div>• Use "Inline" button for images within text</div>
            <div>• Use "Block" button for diagrams at end of question</div>
            <div>• Double-click image to toggle between inline/block</div>
            <div>• Drag images to reposition them</div>
            <div>• Use $...$ for inline math: $x^2 + y^2 = z^2$</div>
            <div>• Use $$...$$ for display math: $$x^2 + y^2 = z^2$$</div>
            <div>• Paste images directly from clipboard</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditorWithImages;

